import React, { useState, useCallback, useEffect } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import Image from "components/Image"
import { CARD_TYPES } from "./ReelsConfig"
import { select } from "utils/sounds"
import useSound from "use-sound"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { styles } from "./styles/MemoramaSettings.styles"
import PropTypes from "prop-types"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useAuthContext } from "contexts/AuthContext"

const TYPES = ["image", "audio", "text"]
const PLACEHOLDER = "Select..."

MemorySettings.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
}

export default function MemorySettings({
  open,
  onClose,
  onSuccess,
  fontSettings = {},
}) {
  const { audioSettings } = useAuthContext()
  const [playSelect] = useSound(select, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const [memoramaSettings, setMemoramaSettings] = useState({
    cardType: CARD_TYPES.IMAGE_TEXT.value,
    pairs: 8,
    isSoundEnabled: true,
  })
  const [cardTypeA, setCardTypeA] = useState(TYPES[0])
  const [cardTypeB, setCardTypeB] = useState(TYPES[2])
  const { t } = useTranslation()

  useEffect(() => {
    return () => {
      setCardTypeA(TYPES[0])
      setCardTypeB(TYPES[2])
      setMemoramaSettings({
        cardType: CARD_TYPES.IMAGE_TEXT.value,
        pairs: 8,
        isSoundEnabled: true,
      }) // reset
    }
  }, [open])

  const onChange = useCallback(
    (cardTypeA, cardTypeB) => {
      if (cardTypeA === "" || cardTypeB === "") return
      // console.log(" ~ cardType:", `${cardTypeA}-${cardTypeB}`)
      // get value from CARD_TYPES
      try {
        let value =
          CARD_TYPES[`${cardTypeA.toUpperCase()}_${cardTypeB.toUpperCase()}`]
            ?.value
        if (!value) {
          // try inverse
          value =
            CARD_TYPES[`${cardTypeB.toUpperCase()}_${cardTypeA.toUpperCase()}`]
              ?.value
        }
        // console.log("~ value:", value)
        if (value >= 0) {
          setMemoramaSettings({
            ...memoramaSettings,
            cardType: value,
          })
        }
      } catch (error) {}
      playSelect()
    },
    [memoramaSettings, playSelect]
  )

  const onChangeA = (event) => {
    setCardTypeA(event.target.value)
    onChange(event.target.value, cardTypeB)
  }

  const onChangeB = (event) => {
    setCardTypeB(event.target.value)
    onChange(cardTypeA, event.target.value)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={"md"}
      PaperProps={{
        sx: [
          styles.contanier,
          fontSettings?.fontFamily && {
            "& .MuiTypography-root, button, textarea": {
              fontFamily: fontSettings.fontFamily,
            },
          },
          fontSettings?.bgColor && {
            backgroundColor: fontSettings.bgColor,
          },
        ],
      }}
    >
      <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
        <ArrowBackIcon fontSize="large" color="primary" />
      </IconButton>
      <Image name="gameMemoramaActive" sx={styles.mainImage} />
      <DialogTitle textAlign={"center"} mt={1}>
        {t("memorama")}
      </DialogTitle>
      <DialogContent>
        <Stack
          direction={"row"}
          justifyContent={"center"}
          alignItems={"center"}
          spacing={2}
        >
          <Accordion defaultExpanded disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="typeA-content"
              id="typeA-header"
              sx={styles.optionsContent}
            >
              <Typography sx={styles.title}>{t(PLACEHOLDER)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl sx={{ width: 200 }}>
                <RadioGroup
                  aria-labelledby="typesA-buttons-group"
                  name="typesA-buttons-group"
                  value={cardTypeA}
                  onChange={onChangeA}
                >
                  {TYPES.map((name) => (
                    <FormControlLabel
                      key={name}
                      value={name}
                      control={<Radio />}
                      label={t(name)}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="typeB-content"
              id="typeB-header"
              sx={styles.optionsContent}
            >
              <Typography sx={styles.title}>{t(PLACEHOLDER)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl sx={{ width: 200 }}>
                <RadioGroup
                  aria-labelledby="typesB-buttons-group"
                  name="typesB-buttons-group"
                  value={cardTypeB}
                  onChange={onChangeB}
                >
                  {TYPES.map((name) => (
                    <FormControlLabel
                      key={name}
                      value={name}
                      control={<Radio />}
                      label={t(name)}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          color="success"
          variant="contained"
          disableElevation
          disabled={cardTypeA === "" || cardTypeB === ""}
          onClick={() => {
            onSuccess(memoramaSettings)
            onClose()
          }}
        >
          {t("ok")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
